/** What a counter is worth to the hero standing in front of it, in gold.
 *
 * The AI's object scorer prices a `bonus` off the stat keys it recognises and
 * falls back to a flat 600 when it recognises none. Every service in
 * `services.ts` hits that fallback, which is why a computer wanders onto a Hill
 * Fort by accident and never walks to one on purpose - and a Warcraft Keep standing
 * next to an army of spearwardens is worth several thousand gold.
 *
 * This is the missing table. It lives in `packages/core` rather than in the
 * planner for one reason: **the price is a fact about the rule**, and the rule
 * is here. A Elder Oak is worth a level because `services.ts` grants a
 * level; a University is worth a skill only when the hero has one left to
 * learn, and `teachable` is what decides that. A second copy in the AI would be
 * a second statement of the rules that could disagree with the first, which is
 * exactly the shape D9 exists to prevent.
 *
 * Returns **null** for a payload that names no service, or one whose service
 * has no honest price - so the caller's own fallback still applies and this
 * file never has to pretend to a number it has not got.
 *
 * Everything here reads and nothing writes. It is safe to call while scoring.
 */

import type { PlayerId } from "../ids.js";
import { asArtifactDefId, asCreatureDefId, asSkillDefId } from "./ids.js";
import type { HeroState, ResourceKind, Resources } from "../state.js";
import { RESOURCES } from "../state.js";
import type { Ctx } from "./ctx.js";
import type { Payload } from "./payload.js";
import { payList, payNum, payPair, payStr, playerMarkKey } from "./payload.js";
import { skillLevel } from "./hero.js";
import { HILL_FORT_TIER_BP } from "./services.js";
import { questKind, questSatisfied } from "./quests.js";

/**
 * The gold a hero would pay for one secondary skill, one hero level and one
 * Marketplace.
 *
 * Not measured - chosen, and chosen against the prices the content already
 * puts on the same things: a University charges 2000 for a skill and a Tree of
 * Knowledge 2000 for a level, so a computer that valued them at exactly their
 * price would be indifferent to every one of them. They are worth walking to,
 * so they are worth more than the ticket.
 */
export const SKILL_VALUE = 4000;
export const LEVEL_VALUE = 5000;
export const MARKETPLACE_VALUE = 1500;
/** A disc of lit map, and the whole plane. Scouting is worth less than an army. */
export const OBSERVATORY_VALUE = 900;
export const CARTOGRAPHER_VALUE = 2500;
/** A hull is worth nothing to a hero with no reason to sail. */
export const SHIPYARD_VALUE = 300;
export const SMALL_FIND_VALUE = 400;

export function serviceValue(c: Ctx, h: HeroState, pay: Payload): number | null {
  const service = payStr(pay, "service");
  if (service === "") return null;
  const seen = pay[playerMarkKey(h.owner)] === true;

  switch (service) {
    case "observatory":
      // Once per kingdom, so a second hero is walking to a door that is shut.
      return seen ? 0 : OBSERVATORY_VALUE;

    case "cartographer": {
      if (seen) return 0;
      const price = payNum(pay, "price", 0);
      return affordable(c, h.owner, { gold: price })
        ? Math.max(0, CARTOGRAPHER_VALUE - price)
        : 0;
    }

    case "university": {
      const price = payNum(pay, "price", 0);
      if (!affordable(c, h.owner, { gold: price })) return 0;
      // Worth a skill only while the hero has one of these left to learn.
      const fresh = payList(pay, "skills").some((s) => {
        const id = asSkillDefId(s);
        return c.content.skills.get(id) !== undefined && skillLevel(h, id) === 0;
      });
      return fresh ? Math.max(0, SKILL_VALUE - price) : 0;
    }

    case "tree": {
      const price = payNum(pay, "price", 0);
      const r = priceResource(pay);
      if (!affordable(c, h.owner, { [r]: price })) return 0;
      const worth = LEVEL_VALUE * Math.max(1, payNum(pay, "levels", 1));
      // Priced in gold, so a price in gems is converted at the content's own
      // exchange rate rather than at one invented here.
      return Math.max(0, worth - price * (c.content.resourceValue[r] ?? 1));
    }

    case "hillfort": return upgradeWorth(c, h);

    case "warfactory": {
      let best = 0;
      for (const entry of payList(pay, "machines")) {
        const { id, n } = payPair(entry);
        const def = c.content.artifacts.get(asArtifactDefId(id));
        if (def === undefined) continue;
        const held = h.artifacts[def.slot];
        if (held !== null && held !== undefined) continue;
        const price = n > 0 ? n : def.cost;
        if (!affordable(c, h.owner, { gold: price })) continue;
        const gain = def.cost - price;
        if (gain > best) best = gain;
      }
      return best;
    }

    case "blackmarket": {
      let best = 0;
      for (const entry of payList(pay, "offers")) {
        const { id, n } = payPair(entry);
        const def = c.content.artifacts.get(asArtifactDefId(id));
        if (def === undefined) continue;
        const price = n > 0 ? n : def.cost;
        if (!affordable(c, h.owner, { gold: price })) continue;
        const gain = def.cost - price;
        if (gain > best) best = gain;
      }
      return best;
    }

    case "market": return seen ? 0 : MARKETPLACE_VALUE;

    case "shipyard": return SHIPYARD_VALUE;

    case "leanto":
    case "wagon":
      return payNum(pay, "emptied", 0) === 1 || pay["emptied"] === true
        ? 0 : SMALL_FIND_VALUE;

    case "seer": return questWorth(c, h, pay);

    case "altar":
    case "siren":
      // Both trade army for experience, and neither is something a planner
      // should be drawn towards: the computer cannot yet judge which of its
      // stacks it can spare, and a scorer that liked them would walk a hero
      // across the map to weaken itself.
      return 0;

    case "event": return 0;

    default: return null;
  }
}

const priceResource = (pay: Payload): ResourceKind => {
  const r = payStr(pay, "priceResource", "gold");
  return RESOURCES.includes(r as ResourceKind) ? (r as ResourceKind) : "gold";
};

function affordable(c: Ctx, owner: PlayerId, cost: Partial<Resources>): boolean {
  const p = c.s.players[owner];
  if (p === undefined) return false;
  for (const r of RESOURCES) {
    const need = cost[r] ?? 0;
    if (need > p.resources[r]) return false;
  }
  return true;
}

/**
 * What the Warcraft Keep would do for the army this hero is actually carrying.
 *
 * The gain is the difference in `aiValue` between the stack and its upgrade,
 * over the whole stack, less what the fort charges - so a fort next to seventy
 * spearwardens prices very differently from the same fort next to seven angels, and
 * a fort next to an army with nothing upgradable prices at zero. That is the
 * whole reason this cannot be a constant.
 */
function upgradeWorth(c: Ctx, h: HeroState): number {
  const army = c.s.armies[h.army];
  if (army === undefined) return 0;
  const upgrades = new Map<string, string>();
  for (const id of [...c.content.creatures.keys()].sort()) {
    const d = c.content.creatures.get(id)!;
    if (d.upgradeOf !== null) upgrades.set(d.upgradeOf as string, id as string);
  }

  let total = 0;
  for (const s of army.slots) {
    if (s === null || s.count <= 0) continue;
    const from = c.content.creatures.get(s.def);
    const toId = upgrades.get(s.def as string);
    if (from === undefined || toId === undefined) continue;
    const to = c.content.creatures.get(asCreatureDefId(toId));
    if (to === undefined) continue;
    const bp = HILL_FORT_TIER_BP[from.tier] ?? 10000;
    let price = 0;
    for (const r of RESOURCES) {
      const diff = (to.cost[r] ?? 0) - (from.cost[r] ?? 0);
      if (diff <= 0) continue;
      price += Math.trunc((diff * s.count * bp) / 10000) * (c.content.resourceValue[r] ?? 1);
    }
    const gain = (to.aiValue - from.aiValue) * s.count - price;
    if (gain > 0) total += gain;
  }
  return total;
}

/**
 * A Oracle's Hut is worth its reward, and only to a hero who can pay the toll
 * today.
 *
 * A hut asking for something the hero has not got is worth nothing *now*, which
 * is the right answer for a planner choosing where to walk this turn - it is
 * not a permanent zero, and the hut prices itself again the moment the army or
 * the level crosses the bar.
 */
function questWorth(c: Ctx, h: HeroState, pay: Payload): number {
  if (pay[`q:${h.owner as string}`] === true) return 0;
  if (questKind(pay) === "none") return 0;
  if (!questSatisfied(c, h, pay)) return 0;

  let worth = payNum(pay, "gold", 0);
  worth += Math.trunc(payNum(pay, "experience", 0) / 2);
  const art = payStr(pay, "artifact");
  if (art !== "") worth += c.content.artifacts.get(asArtifactDefId(art))?.cost ?? 0;
  const resource = payStr(pay, "resource");
  const amount = payNum(pay, "amount", 0);
  if (resource !== "" && RESOURCES.includes(resource as ResourceKind)) {
    worth += amount * (c.content.resourceValue[resource as ResourceKind] ?? 1);
  }

  // Deliveries are taken, so the toll comes off the price. Thresholds are only
  // looked at and cost nothing.
  if (questKind(pay) === "resources") {
    const r = payStr(pay, "questResource") as ResourceKind;
    if (RESOURCES.includes(r)) {
      worth -= payNum(pay, "questAmount", 0) * (c.content.resourceValue[r] ?? 1);
    }
  }
  if (questKind(pay) === "artifact") {
    const want = payStr(pay, "questArtifact");
    worth -= c.content.artifacts.get(asArtifactDefId(want))?.cost ?? 0;
  }
  if (questKind(pay) === "creatures") {
    const def = c.content.creatures.get(asCreatureDefId(payStr(pay, "questCreature")));
    worth -= (def?.aiValue ?? 0) * payNum(pay, "questCount", 0);
  }
  return worth > 0 ? worth : 0;
}
